// src/components/dashboard/AlertsWidget/AlertsWidget.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileService } from '../../../services/profileService';
import { watchlistService } from '../../../services/watchlistService';
import './AlertsWidget.css';

const AlertsWidget = () => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUnreadAlertsCount();

    // Re-fetch whenever alerts are refreshed externally
    // (e.g. after marking notifications read on the alerts tab,
    //  or after a copy trade fires and CopyTradeWidget dispatches this event)
    const handleRefreshAlerts = () => fetchUnreadAlertsCount();
    window.addEventListener('refreshAlerts', handleRefreshAlerts);
    return () => window.removeEventListener('refreshAlerts', handleRefreshAlerts);
  }, []);

  const fetchUnreadAlertsCount = async () => {
    try {
      // Fetch unread counts from ALL notification sources in parallel.
      // Every source must be included — missing any one causes the badge
      // to show a lower count than what the user actually sees in the alerts tab.
      const [
        investmentPlanAlerts,
        walletNotifications,
        depositNotifications,
        withdrawalNotifications,
        priceAlerts,
      ] = await Promise.allSettled([
        profileService.getAlerts({ is_read: false }),
        profileService.getWalletNotifications({ is_read: false }),
        profileService.getDepositNotifications({ is_read: false }),
        profileService.getWithdrawalNotifications({ is_read: false }),
        watchlistService.getPriceAlerts({ is_read: false }),
      ]);

      // Promise.allSettled returns { status, value } objects.
      // Use optional chaining so a single failing source never zeroes the total.
      const safeLen = (result) =>
        result.status === 'fulfilled' ? (result.value?.length ?? 0) : 0;

      const total =
        safeLen(investmentPlanAlerts) +
        safeLen(walletNotifications) +
        safeLen(depositNotifications) +
        safeLen(withdrawalNotifications) +
        safeLen(priceAlerts);

      setUnreadCount(total);
    } catch (err) {
      console.error('Error fetching unread alerts count:', err);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    navigate('/profile', { state: { activeTab: 'alerts' } });
  };

  return (
    <div className="alerts-widget-container">
      <button
        className="alerts-icon-button"
        onClick={handleClick}
        title={
          unreadCount > 0
            ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`
            : 'No unread notifications'
        }
      >
        {/* Bell Icon Symbol */}
        <div className="alerts-symbol">
          <div className="bell-top"></div>
          <div className="bell-body"></div>
          <div className="bell-clapper"></div>
        </div>

        {/* Red dot indicator with count when there are unread alerts */}
        {unreadCount > 0 && (
          <span className="unread-indicator">{unreadCount}</span>
        )}
      </button>

      <span className="widget-label">Alerts</span>
    </div>
  );
};

export default AlertsWidget;